/**
 * Sidebar conversation drag-reorder hook. Owns the conversation DnD state
 * (`draggedIndex`, `tempConversationOrder`), the temp-order reconcile effect,
 * and the four pointer handlers.
 *
 * Hook-order safety: place this right after `userConversations` is derived and
 * before consumers that read `tempConversationOrder` (the
 * `displayedConversations` memo, the context-menu pin handler), so the
 * render-to-render hook order stays stable.
 *
 * The live splice-reorder and the cache-caught-up check are delegated to the
 * `reorderByIndex` / `sameOrderById` kernels (pinned by dragReorder.test.ts).
 */

import type React from 'react';
import { useEffect, useState } from 'react';
import type { useReorderConversationsMutation } from '@/features/conversations/hooks/useReorderConversationsMutation';
import type { Conversation } from '@/types/conversations';
import { reorderByIndex, sameOrderById } from '../dragReorder';

interface UseDragReorderArgs {
  userConversations: Conversation[];
  orderedUserConversations: Conversation[];
  reorderMutation: ReturnType<typeof useReorderConversationsMutation>;
}

export function useDragReorder({
  userConversations,
  orderedUserConversations,
  reorderMutation,
}: UseDragReorderArgs) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [tempConversationOrder, setTempConversationOrder] = useState<Conversation[] | null>(null);

  // Only clear temporary order once the query cache has caught up to the committed order.
  useEffect(() => {
    if (!tempConversationOrder) return;
    if (sameOrderById(tempConversationOrder, orderedUserConversations)) {
      setTempConversationOrder(null);
    }
  }, [tempConversationOrder, orderedUserConversations]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    const conversations = tempConversationOrder ?? userConversations;
    const newOrder = reorderByIndex(conversations, draggedIndex, index);

    setTempConversationOrder(newOrder);
    setDraggedIndex(index);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    setDraggedIndex(null);

    // Save the final order if there were changes
    if (tempConversationOrder) {
      const newOrder = tempConversationOrder;

      // Optimistic cache update happens in onMutate (immediate)
      reorderMutation.mutate(newOrder.map((conversation) => conversation.id), {
        onError: () => {
          // On error, clear temp order to show rolled-back cache state
          console.error('[Desktop Reorder] Failed to save conversation order');
          setTempConversationOrder(null);
        },
      });
    } else {
      setTempConversationOrder(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setTempConversationOrder(null);
  };

  return {
    draggedIndex,
    tempConversationOrder,
    setTempConversationOrder,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
}
